import {
  Box,
  ClickAwayListener,
  InputBase,
  type InputBaseComponentProps,
  type SxProps,
  TextareaAutosize,
  type Theme,
  Typography,
  useTheme,
} from "@mui/material";
import {
  type ElementRef,
  Fragment,
  forwardRef,
  type MouseEvent,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { splitTextWithLinks } from "utilz/helpers";

export interface EditableChipProps {
  isEditMode?: boolean;
  value: string;
  onEdit: (newValue: string) => void;
  inputSx?: SxProps<Theme> | any;
  typographySx?: SxProps<Theme>;
  disabled?: boolean;
  onChange?: (value: string | undefined, isEdit: boolean) => void;
  onNavigate?: () => void;
  placeholder?: string;
  inputProps?: InputBaseComponentProps | undefined;
  sx?: SxProps<Theme> | any;
  multiline?: boolean;
  onBlur?: () => void;
  validator?: (value: string) => boolean;
  error?: boolean;
  errorMessage?: string;
  fullWidth?: boolean;
  type?: string;
  onClick?: () => void;
  nullable?: boolean;
  isError?: boolean;

  typographyValueRenderer?: (value: any) => string;
  hideHelperBox?: boolean;
  /** Renders URLs inside the display text as clickable links. Off by default
   *  so existing consumers keep plain-text behaviour. */
  linkify?: boolean;
}

/** Display-mode text with URLs turned into real anchors. Clicks on a link stop
 *  at the anchor so the chip does not flip into edit mode. */
const LinkifiedText = ({ text }: { text: string }) => {
  const theme = useTheme();
  return (
    <>
      {splitTextWithLinks(text).map((segment, index) =>
        segment.href ? (
          <Box
            component="a"
            key={`${index}-${segment.text}`}
            href={segment.href}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(event: MouseEvent) => event.stopPropagation()}
            sx={{
              color: theme.palette.primary.main,
              textDecoration: "underline",
              wordBreak: "break-all",
              cursor: "pointer",
            }}
          >
            {segment.text}
          </Box>
        ) : (
          <Fragment key={`${index}-${segment.text}`}>{segment.text}</Fragment>
        ),
      )}
    </>
  );
};
export const EditableChip = forwardRef(
  (
    {
      inputProps,
      isEditMode = false,
      onEdit,
      value = "",
      inputSx,
      typographySx,
      disabled = false,
      onChange,
      onNavigate,
      placeholder = "",
      sx,
      multiline,
      onBlur,
      validator,
      error = false,
      errorMessage,
      fullWidth = true,
      type,
      onClick,
      nullable = false,
      isError,
      typographyValueRenderer,
      hideHelperBox = false,
      linkify = false,
    }: EditableChipProps,
    ref: any,
  ) => {
    const inputRef = useRef<ElementRef<"input">>(null);
    const submitTimerRef = useRef<ReturnType<typeof setTimeout>>();
    const pendingSubmitRef = useRef<(() => void) | null>(null);
    const hasSubmittedRef = useRef(false);
    const [isEdit, setIsEdit] = useState(isEditMode);
    const [data, setData] = useState(value);

    const theme = useTheme();

    // Expose edit method through ref
    useImperativeHandle(ref, () => ({
      focus: () => {
        setIsEdit(true);
      },
    }));
    useEffect(() => {
      // A new edit session re-arms the submit guard.
      if (isEdit) {
        hasSubmittedRef.current = false;
      }
      setTimeout(() => {
        const field = inputRef?.current;
        field?.focus();
        // focus() alone drops the caret at position 0, so a click to edit puts
        // you before the existing text. Park it at the end instead.
        const caret = field?.value?.length ?? 0;
        try {
          field?.setSelectionRange(caret, caret);
        } catch {
          // Inputs like type="number"/"email" reject selection ranges.
        }
      }, 50);
    }, [isEdit]);

    // Unmounting mid-submit must not swallow the edit: checklist rows remount
    // on every refetch, so a refetch landing inside the 50ms window would drop
    // the save silently. Flush it instead of cancelling it.
    useEffect(
      () => () => {
        clearTimeout(submitTimerRef.current);
        const pending = pendingSubmitRef.current;
        pendingSubmitRef.current = null;
        pending?.();
      },
      [],
    );

    // Leaving the field fires blur AND click-away (and Enter fires a third
    // time). The old 50ms debounce only coalesced them when they landed close
    // together, so a slow click submitted twice — two API calls, two toasts.
    // One submit per edit session, regardless of timing.
    const onSubmit = () => {
      if (hasSubmittedRef.current) return;
      hasSubmittedRef.current = true;
      clearTimeout(submitTimerRef.current);
      const submit = () => {
        if (!nullable && (data.trim().length <= 0 || data.trim() === value)) {
          setData(value);
          onEdit(value);
          onChange?.(value, false);
          onBlur?.();
        } else {
          if (validator && !validator(data.trim())) {
            setData(value);
            onEdit(value);
            onChange?.(value, false);
            onBlur?.();
            setIsEdit(false);
            return;
          }
          onEdit(data.trim());
          onChange?.(data.trim(), false);
          onBlur?.();
        }
        setIsEdit(false);
      };
      pendingSubmitRef.current = submit;
      submitTimerRef.current = setTimeout(() => {
        pendingSubmitRef.current = null;
        submit();
      }, 50);
    };

    useEffect(() => {
      setIsEdit(isEditMode);
    }, [isEditMode]);

    useEffect(() => {
      setData(value);
    }, [value]);

    return (
      <Box
        sx={{
          overflow: "hidden",
          minWidth: "0px",
          ...(fullWidth ? { flex: 1 } : { minWidth: "150px" }),
          height: "fit-content",
          width: "max-content",
          paddingRight: "24px",
          alignItems: "center",
          // cursor: "text",
          "& .custom-input-area": {
            outline: "none",
            my: "4px",
            borderRadius: "4px",
            width: "100%",
            bgcolor: isError
              ? theme.palette.error.tint
              : theme.palette.neutral["B-005"],
            ...theme.typography["subtitle1"],
            px: "8px",
            border: "none",
            resize: "none",

            ...inputSx,
          },

          ...sx,
        }}
        // onClick={onClick}
      >
        {isEdit && !disabled ? (
          <ClickAwayListener
            onClickAway={(e) => {
              onSubmit();
            }}
          >
            {multiline ? (
              <TextareaAutosize
                className="custom-input-area"
                autoComplete="off"
                name={"task"}
                style={{
                  ...inputSx,
                }}
                placeholder={placeholder}
                ref={inputRef}
                value={data}
                onBlur={() => {
                  onSubmit();
                }}
                onKeyDown={(ev) => {
                  ev.stopPropagation();
                  if (ev.key.toLocaleLowerCase() === "enter") {
                    ev.preventDefault();
                    onSubmit();
                  }
                }}
                onKeyUp={(ev) => {
                  ev.preventDefault();
                }}
                onChange={(event) => {
                  setData(event.target.value);
                  onChange?.(event.target.value, true);

                  event.stopPropagation();
                  event.preventDefault();
                }}
              />
            ) : (
              <InputBase
                placeholder={placeholder}
                inputRef={inputRef}
                type={type}
                sx={{
                  ...(fullWidth ? { width: "100%" } : { minWidth: "150px" }),
                  bgcolor: isError
                    ? theme.palette.error.tint
                    : theme.palette.neutral["B-005"],
                  // minwidth: "100px",
                  ...theme.typography["subtitle1"],
                  px: "8px",
                  ...(error && {
                    border: `1px solid ${theme.palette.error.main}`,
                  }),
                  borderRadius: "4px",

                  ...inputSx,
                }}
                name={"task"}
                value={data}
                onBlur={() => {
                  onSubmit();
                }}
                onKeyDown={(ev) => {
                  ev.stopPropagation();
                  if (ev.key.toLocaleLowerCase() === "enter") {
                    ev.preventDefault();
                    onSubmit();
                  }
                }}
                onKeyUp={(ev) => {
                  ev.preventDefault();
                }}
                inputProps={inputProps}
                onChange={(event) => {
                  setData(event.target.value);
                  onChange?.(event.target.value, true);
                  event.stopPropagation();
                  event.preventDefault();
                }}
              />
            )}
          </ClickAwayListener>
        ) : (
          <Typography
            onClick={(e) => {
              if (!disabled) {
                e.stopPropagation();
                e.preventDefault();
              }
              if (onNavigate) {
                onNavigate();
                return;
              } else {
                setIsEdit(true);
              }
            }}
            variant="subtitle1"
            sx={{
              ...(fullWidth ? { width: "100%" } : {}),
              minWidth: "150px",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: multiline ? "normal" : "nowrap",
              maxWidth: "min-content",
              borderRadius: "4px",
              my: multiline ? "4px" : "0px",
              px: "8px",
              cursor: disabled ? "inherit" : "text",
              border: error ? `1px solid ${theme.palette.error.main}` : "none",
              ":hover": {
                ...(onNavigate && {
                  color: theme.palette.primary.main,
                  textDecoration: "underline",
                  cursor: "pointer",
                }),
              },
              ...typographySx,
            }}
          >
            {data ? (
              typographyValueRenderer ? (
                typographyValueRenderer(data)
              ) : linkify ? (
                <LinkifiedText text={data} />
              ) : (
                data
              )
            ) : (
              placeholder
            )}
          </Typography>
        )}
        <Box
          sx={{
            pl: "14px",
            mt: errorMessage ? "4px" : hideHelperBox ? "0px" : "4px",
          }}
        >
          <Typography variant="body-small" color="error">
            {errorMessage}
          </Typography>
        </Box>
      </Box>
    );
  },
);
